/**
 * useDepositActions — per-gép kaució refund/retain write-hook (Slice A).
 *
 * A use-rental-payment.ts mintáját követi: useState(loading) + useState(error) +
 * async try/catch/finally, a hibát a hívónak THROW-olja (a hívó toastol).
 */
import { useState } from 'react';
import {
  type PartialRefundDepositDto,
  type PartialRefundDepositResult,
  type RefundDepositDto,
  type RetainDepositDto,
  type TransferToWorksheetDto,
  type TransferToWorksheetResult,
  partialRefundDeposit,
  refundDeposit,
  retainDeposit,
  transferDepositToWorksheet,
} from '../../../api/deposits';

export interface UseDepositActionsResult {
  refund: (depositId: string, dto: RefundDepositDto) => Promise<{ data: unknown }>;
  retain: (depositId: string, dto: RetainDepositDto) => Promise<{ data: unknown }>;
  partialRefund: (
    depositId: string,
    dto: PartialRefundDepositDto
  ) => Promise<{ data: PartialRefundDepositResult }>;
  transferToWorksheet: (
    depositId: string,
    dto: TransferToWorksheetDto
  ) => Promise<{ data: TransferToWorksheetResult }>;
  loading: boolean;
  error: string | null;
}

export function useDepositActions(): UseDepositActionsResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refund = async (depositId: string, dto: RefundDepositDto): Promise<{ data: unknown }> => {
    setLoading(true);
    setError(null);
    try {
      return await refundDeposit(depositId, dto);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const retain = async (depositId: string, dto: RetainDepositDto): Promise<{ data: unknown }> => {
    setLoading(true);
    setError(null);
    try {
      return await retainDeposit(depositId, dto);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const partialRefund = async (
    depositId: string,
    dto: PartialRefundDepositDto
  ): Promise<{ data: PartialRefundDepositResult }> => {
    setLoading(true);
    setError(null);
    try {
      return await partialRefundDeposit(depositId, dto);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const transferToWorksheet = async (
    depositId: string,
    dto: TransferToWorksheetDto
  ): Promise<{ data: TransferToWorksheetResult }> => {
    setLoading(true);
    setError(null);
    try {
      return await transferDepositToWorksheet(depositId, dto);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { refund, retain, partialRefund, transferToWorksheet, loading, error };
}
